import { useCallback } from 'react'
import { environment, useDataLakeContext } from 'react-common'
import { eventLogger, EventNames, MappedUser } from 'apps-common'
import { Activity } from '@/api/models/activity'
import { Objective } from '@/api/models/objective'
import { useAuth } from '@/features/auth/useAuth'
import { useDataLakeContextVariables } from '@/hooks/eventLogger/useDataLakeContextVariables'
import { useIsMobile } from '@/hooks/useIsMobile'
import i18n from '@/plugins/i18n'

const appVersion = environment().VITE_RELEASE_NUMBER

type FeedbackEntities = {
  grade?: number
  details: Record<string, boolean>
  comment: string
}

type Entities<T> = T extends 'product_feedback'
  ? FeedbackEntities
  : { objective?: Objective; activity?: Activity; certificateTemplates?: string[] }

export const useEventLogger = () => {
  const { user } = useAuth()
  const isMobile = useIsMobile()

  const dataLakeContextVariables = useDataLakeContextVariables()
  const { logDataLakeEvent } = useDataLakeContext()

  const logEvent = useCallback(
    async <T extends EventNames>(eventName: T, eventProperties?: Entities<T>) => {
      const mappedUser = {
        id: user?.id,
        mobile_web: isMobile,
        role: 'learner',
        locale: i18n.language,
        app_version: appVersion,
        tenant: user?.tenant_id,
      } as MappedUser

      if (eventName === 'product_feedback') {
        logDataLakeEvent(eventName, eventProperties)
      } else {
        logDataLakeEvent(eventName, dataLakeContextVariables)
      }

      return await eventLogger({ eventName, eventProperties: { user: mappedUser, ...eventProperties } })
    },
    [user, isMobile, logDataLakeEvent, dataLakeContextVariables],
  )

  return {
    logEvent,
  }
}
